class CustomSelect extends HTMLElement {
  connectedCallback() {
    this.details = this.querySelector('details');
    this.summary = this.querySelector('summary');
    this.list = this.querySelector('[role="listbox"]');
    this.input = this.querySelector('input[type="hidden"]');
    this.currentLabel = this.querySelector('.custom-select__current');

    this._onSummaryClick = this.onSummaryClick.bind(this);
    this._onOptionClick = this.onOptionClick.bind(this);
    this._onKeydown = this.onKeydown.bind(this);
    this._onDocumentClick = this.onDocumentClick.bind(this);

    this.summary.addEventListener('click', this._onSummaryClick);
    this.list.addEventListener('click', this._onOptionClick);
    this.list.addEventListener('keydown', this._onKeydown);
    document.addEventListener('click', this._onDocumentClick);
  }

  disconnectedCallback() {
    this.summary.removeEventListener('click', this._onSummaryClick);
    this.list.removeEventListener('click', this._onOptionClick);
    this.list.removeEventListener('keydown', this._onKeydown);
    document.removeEventListener('click', this._onDocumentClick);
  }

  get options() {
    return Array.from(this.querySelectorAll('[role="option"]'));
  }

  onSummaryClick(event) {
    event.preventDefault();
    this.details.open ? this.close() : this.open();
  }

  open() {
    this.details.setAttribute('open', '');
    this.summary.setAttribute('aria-expanded', 'true');

    // Position the list so the selected option sits directly over the summary.
    // offsetTop is measured from the list's top, so shifting the list up by that
    // amount places the selected item at the same Y as the summary.
    requestAnimationFrame(() => {
      const selected = this.list.querySelector('[aria-selected="true"]') || this.options[0];
      if (selected) {
        const extraUp = this.summary.offsetHeight / 2;
        const leftInset = parseFloat(getComputedStyle(selected).paddingLeft) || 0;
        this.list.style.top = `${-selected.offsetTop - extraUp}px`;
        this.list.style.left = `-${leftInset}px`;
        this.list.style.bottom = 'auto';
        selected.focus();
      }
    });
  }

  close() {
    this.details.removeAttribute('open');
    this.summary.setAttribute('aria-expanded', 'false');
    this.list.style.top = '';
    this.list.style.left = '';
    this.list.style.bottom = '';
  }

  onOptionClick(event) {
    const option = event.target.closest('[role="option"]');
    if (option) this.select(option);
  }

  onKeydown(event) {
    const options = this.options;
    const idx = options.indexOf(document.activeElement);

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        options[Math.min(idx + 1, options.length - 1)].focus();
        break;
      case 'ArrowUp':
        event.preventDefault();
        options[Math.max(idx - 1, 0)].focus();
        break;
      case 'Home':
        event.preventDefault();
        options[0].focus();
        break;
      case 'End':
        event.preventDefault();
        options[options.length - 1].focus();
        break;
      case 'Enter':
      case ' ':
        event.preventDefault();
        if (document.activeElement.getAttribute('role') === 'option') {
          this.select(document.activeElement);
        }
        break;
      case 'Escape':
        event.preventDefault();
        this.close();
        this.summary.focus();
        break;
      case 'Tab':
        this.close();
        break;
    }
  }

  onDocumentClick(event) {
    if (!this.contains(event.target)) this.close();
  }

  select(option) {
    this.options.forEach((opt) => {
      opt.setAttribute('aria-selected', 'false');
      opt.classList.remove('custom-select__option--selected');
    });
    option.setAttribute('aria-selected', 'true');
    option.classList.add('custom-select__option--selected');

    const label = option.textContent.trim();
    this.currentLabel.textContent = label;
    this.summary.setAttribute('aria-label', `${this.list.getAttribute('aria-label')}: ${label}`);

    this.input.value = option.dataset.value;
    this.input.dispatchEvent(new Event('input', { bubbles: true }));

    this.close();
    this.summary.focus();
  }
}

customElements.define('custom-select', CustomSelect);
